import React from "react";
import { Card, Row, Col } from "react-bootstrap";
import ReactEcharts from "echarts-for-react";
import { useTranslation } from "react-i18next";
import { GREEN, YELLOW, RED, GREY, BLUE, shadeColor } from "@helper/colors";

const style = {
  height: "420px",
  width: "100%",
};

export default function HealthPieChart({ treeStatus }) {
  const { t } = useTranslation();
  const healthArray = Array.isArray(treeStatus) ? treeStatus : [];
  const healthMap = !Array.isArray(treeStatus) && treeStatus ? treeStatus : {};
  const useDryingStates =
    healthArray.length >= 6 ||
    "drying_out" in healthMap ||
    "dry" in healthMap ||
    "too_wet" in healthMap ||
    "5" in healthMap;

  const getValue = (primaryKey, indexKey, fallbackKey) => {
    const fallback =
      typeof fallbackKey === "number"
        ? healthArray[fallbackKey]
        : fallbackKey
        ? healthMap[fallbackKey]
        : undefined;
    const indexed = typeof indexKey === "number" ? healthArray[indexKey] : healthMap[indexKey];
    const direct = typeof primaryKey === "number" ? healthArray[primaryKey] : healthMap[primaryKey];
    return direct ?? indexed ?? fallback ?? 0;
  };

  const healthyValue = useDryingStates ? getValue("healthy", 0, "0") : getValue("healthy", 0, "0");
  const mediumValue = useDryingStates ? getValue("drying_out", 1, "1") : getValue("medium", 1, "1");
  const criticalValue = useDryingStates ? getValue("dry", 2, "2") : getValue("critical", 2, "2");
  const unknownValue = useDryingStates ? getValue("unknown", 3, "3") : getValue("unknown", 3, "3");
  const noSensorValue = getValue("no_sensor");
  const noDataValue = getValue("no_data");
  const maintenanceNeededValue = getValue("maintenance_needed");
  const frostValue = useDryingStates ? getValue("frost", 4, "4") : getValue("frost", 4, "4");
  const tooWetValue = getValue("too_wet", 5, "5");

  // Use subcategories if any exist, otherwise use legacy unknown value
  const hasSubcategories = noSensorValue > 0 || noDataValue > 0 || maintenanceNeededValue > 0;
  const data = [
    {
      name: `${t("health_states.healthy")}`,
      value: healthyValue,
      color: GREEN,
      hoverColor: shadeColor(GREEN, 20),
      itemStyle: {
        color: GREEN,
      },
    },
    {
      name: `${t("health_states.medium")}`,
      value: mediumValue,
      color: YELLOW,
      hoverColor: shadeColor(YELLOW, 20),
      itemStyle: {
        color: YELLOW,
      },
    },
    {
      name: `${t("health_states.critical")}`,
      value: criticalValue,
      color: RED,
      hoverColor: shadeColor(RED, 20),
      itemStyle: {
        color: RED,
      },
    },
    {
      name: `${t("health_states.frost")}`,
      value: frostValue,
      color: BLUE,
      hoverColor: shadeColor(BLUE, 20),
      itemStyle: {
        color: BLUE,
      },
    },
  ];

  // Add subcategories if available, otherwise add generic unknown
  if (hasSubcategories) {
    if (noSensorValue > 0) {
      data.push({
        name: `${t("health_states.no_sensor")}`,
        value: noSensorValue,
        color: GREY,
        hoverColor: shadeColor(GREY, 20),
        itemStyle: {
          color: GREY,
        },
      });
    }
    if (noDataValue > 0) {
      data.push({
        name: `${t("health_states.no_data")}`,
        value: noDataValue,
        color: shadeColor(GREY, -15),
        hoverColor: shadeColor(GREY, 5),
        itemStyle: {
          color: shadeColor(GREY, -15),
        },
      });
    }
    if (maintenanceNeededValue > 0) {
      data.push({
        name: `${t("health_states.maintenance_needed")}`,
        value: maintenanceNeededValue,
        color: shadeColor(GREY, -30),
        hoverColor: shadeColor(GREY, -10),
        itemStyle: {
          color: shadeColor(GREY, -30),
        },
      });
    }
  } else if (unknownValue > 0) {
    data.push({
      name: `${t("health_states.unknown")}`,
      value: unknownValue,
      color: GREY,
      hoverColor: shadeColor(GREY, 20),
      itemStyle: {
        color: GREY,
      },
    });
  }

  if (useDryingStates && tooWetValue) {
    data.push({
      name: `${t("health_states.too_wet")}`,
      value: tooWetValue,
      color: shadeColor(BLUE, 10),
      hoverColor: shadeColor(BLUE, 20),
      itemStyle: {
        color: shadeColor(BLUE, 10),
      },
    });
  }

  const option = {
    tooltip: {
      trigger: "item",
    },
    legend: {
      show: false,
    },
    grid: {
      containLabel: true,
    },
    series: [
      {
        name: t("water_stress"),
        type: "pie",
        radius: ["62%", "90%"],
        center: ["50%", "48%"],
        avoidLabelOverlap: false,
        label: {
          show: false,
          position: "center",
        },
        emphasis: {
          label: {
            show: true,
            fontSize: "20",
          },
        },
        labelLine: {
          show: false,
        },
        data: data,
      },
    ],
  };

  const totalHealthState = data.reduce((accumulator, entry) => {
    return accumulator + (Number(entry.value) || 0);
  }, 0);

  return (
    <Card role="dashboard" className="dashboard-panel">
      <Card.Header className="dashboard-panel-header">
        <h6 className="mb-0">{t("dashboard.tree_health")}</h6>
      </Card.Header>
      <Card.Body>
        <ReactEcharts option={option} style={style} notMerge />
        <div className="dashboard-health-overview">
          <div className="dashboard-health-title">{t("dashboard.health_overview")}</div>
          <Row>
            {data.map((entry, index) => {
              const percentage =
                totalHealthState > 0 ? Math.round((entry.value / totalHealthState) * 100) : 0;
              return (
                <Col lg="4" md={6} xs={6} key={index} className="dashboard-health-item">
                  <span className="dashboard-health-label">{entry.name}</span>
                  <div className="dashboard-health-stats">
                    <span
                      className="dashboard-health-dot"
                      style={{ background: entry.itemStyle.color }}
                    />
                    <span className="dashboard-health-value">{entry.value}</span>
                    <span className="dashboard-health-percent">{percentage}%</span>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </Card.Body>
    </Card>
  );
}
